// src/hooks/usePushNotifications.js
//
// ============================================================
// WHAT THIS FILE DOES
//
// Handles the full push notification sign-up process using
// OneSignal. It lets the user turn notifications on or off
// and sends a test notification to confirm everything works.
//
// KEY DETAIL ABOUT COLUMN NAMES
// The React app saves the subscription ID to:
//   notification_preferences.react_subscription_id
// The vanilla JS app saves to:
//   notification_preferences.onesignal_subscription_id
// These are two separate columns on purpose. That way the two
// apps can never overwrite each other, and the Edge Function
// knows which column to read for each app.
//
// WHAT THIS HOOK GIVES YOU
//   isSupported          -- true if this browser can receive push
//   permission           -- 'default', 'granted', or 'denied'
//   isSubscribed         -- true if this device is opted in
//   isLoading            -- true while subscribe/unsubscribe runs
//   error                -- the last error message, or null
//   subscribe            -- turn notifications ON
//   unsubscribe          -- turn notifications OFF
//   sendTestNotification -- send a test push to this device
// ============================================================

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
// the base URL for this project's Supabase instance
// used to build the Edge Function URL below


// ============================================================
// waitForOneSignal
//
// OneSignal loads asynchronously from a script tag in index.html.
// We cannot call OneSignal methods until it has fully loaded.
//
// HOW IT WORKS
//   If OneSignal is already loaded (window.OneSignal exists),
//   resolve right away.
//
//   If not, push a callback into OneSignalDeferred (a queue
//   OneSignal drains once it finishes loading) and wait.
//
//   If it never loads within timeoutMs, reject with a clear
//   error so the user sees a real message instead of a hang.
//
// WHY NOT JUST USE window.OneSignalDeferred ALWAYS
//   The deferred queue is only drained ONCE on first init.
//   After that, pushing a new callback never runs it.
//   So if OneSignal is already ready we must skip the queue
//   and resolve immediately.
// ============================================================
function waitForOneSignal(timeoutMs = 8000) {
  return new Promise(function (resolve, reject) {

    if (window.OneSignal) {
      // OneSignal is already loaded -- no need to wait
      resolve(window.OneSignal)
      return
    }

    // Set a timer so we do not wait forever if the SDK never loads
    const timer = setTimeout(() => {
      reject(new Error(
        'OneSignal did not initialize in time. ' +
        'Make sure OneSignalSDKWorker.js is in your /public folder and ' +
        'REACT_APP_ONESIGNAL_APP_ID is set correctly.'
      ))
    }, timeoutMs)
    // timeoutMs defaults to 8000 = 8 seconds

    // Make sure the deferred queue array exists
    window.OneSignalDeferred = window.OneSignalDeferred || []

    // Push our callback into the queue
    // OneSignal calls it automatically once the SDK is ready
    window.OneSignalDeferred.push(function (OneSignal) {
      clearTimeout(timer)
      // cancel the timeout since we got a response in time
      resolve(OneSignal)
    })
  })
}


// ============================================================
// _writeSubscriptionId
//
// Saves the OneSignal subscription ID into the user's
// notification_preferences row in Supabase.
//
// Uses upsert so:
//   - If a row already exists for this user, it updates it.
//   - If no row exists yet, it creates one.
// onConflict: 'user_id' = the unique column to check for conflicts
//
// Returns { success: boolean, error: string | null }
// so the caller can tell whether the save worked.
// ============================================================
async function _writeSubscriptionId(userId, subId) {
  const { error } = await supabase
    .from('notification_preferences')
    .upsert(
      {
        user_id: userId,
        // identifies which row belongs to this user
        react_subscription_id: subId,
        // the OneSignal subscription ID for this device on the React app
      },
      { onConflict: 'user_id' }
      // if a row already exists with this user_id, update it
      // if no row exists yet, insert a new one
    )

  if (error) {
    console.error('[OneSignal] Error saving subscription ID:', error.message)
    return { success: false, error: error.message }
    // return a failure object so the caller can surface the message
  }

  console.log('[OneSignal] Subscription ID saved to react_subscription_id:', subId)
  return { success: true, error: null }
}


// ============================================================
// saveSubscriptionId
//
// After the user grants permission, OneSignal needs a moment
// to register the device and generate a subscription ID.
// This function waits for that ID to be ready, then saves it.
//
// STRATEGY
//   Step 1: Poll up to 10 times, 500ms apart (5 seconds total).
//           Most devices will be ready within the first 2-3 polls.
//
//   Step 2: If still not ready after polling, register a one-time
//           'change' event listener. OneSignal fires this event
//           the moment the subscription ID becomes available.
//           We also set a 10-second timeout here so we do not
//           wait forever if the event never fires.
//
// Returns { success: boolean, error: string | null }
// ============================================================
async function saveSubscriptionId() {
  const { data: { user } } = await supabase.auth.getUser()
  // get the currently logged-in user so we can save to their row

  if (!user) {
    console.warn('[OneSignal] Cannot save subscription ID; no user logged in.')
    return { success: false, error: 'Not logged in.' }
  }

  const OneSignal = await waitForOneSignal()
  // wait for the SDK to be ready before reading from it

  let subId = null

  // Poll up to 10 times, waiting 500ms between each attempt
  for (let attempt = 0; attempt < 10; attempt++) {
    subId = OneSignal.User.PushSubscription.id
    // .id = the subscription ID, or null if not ready yet
    if (subId) break
    // break out of the loop as soon as we have an ID
    await new Promise(resolve => setTimeout(resolve, 500))
    // wait 500ms before trying again
  }

  if (!subId) {
    // Polling did not work. Fall back to the 'change' event.
    console.warn('[OneSignal] Subscription ID not ready after polling; waiting for change event.')

    // Wrap the event listener in a Promise so we can await it
    const result = await new Promise((resolve) => {

      // Give up after 10 seconds if the event never fires
      const giveUp = setTimeout(() => {
        OneSignal.User.PushSubscription.removeEventListener('change', handler)
        // clean up the listener so it does not fire later accidentally
        resolve({
          success: false,
          error:
            'Could not get a subscription ID from OneSignal. ' +
            'Make sure OneSignalSDKWorker.js is deployed and ' +
            'REACT_APP_ONESIGNAL_APP_ID matches your OneSignal dashboard.',
        })
      }, 10000)
      // 10000ms = 10 seconds

      // This handler runs when OneSignal fires the 'change' event
      const handler = async () => {
        clearTimeout(giveUp)
        // cancel the 10-second timeout since we got a response

        const lateId = OneSignal.User.PushSubscription.id
        // read the ID now that the change event has fired

        if (!lateId) {
          // The event fired but the ID is still empty. Should not happen.
          resolve({ success: false, error: 'Subscription change fired but ID is still null.' })
          return
        }

        // Remove the listener so it does not accidentally fire again
        OneSignal.User.PushSubscription.removeEventListener('change', handler)

        // Save the ID to Supabase now that we have it
        const writeResult = await _writeSubscriptionId(user.id, lateId)
        resolve(writeResult)
      }

      // Attach the listener and wait for the event
      OneSignal.User.PushSubscription.addEventListener('change', handler)
    })

    return result
  }

  // We got the ID from polling. Save it now.
  return await _writeSubscriptionId(user.id, subId)
}


// ============================================================
// verifySubscriptionSaved
//
// After subscribe() runs, this reads back react_subscription_id
// from Supabase to confirm it was actually stored correctly.
//
// WHY THIS IS NEEDED
//   Sometimes the upsert in _writeSubscriptionId silently does
//   nothing if the notification_preferences row does not exist
//   yet (first-time user who subscribed before saving prefs).
//   This function catches that case.
//
// If the row is missing entirely, this creates a minimal row
// so the subscription ID has somewhere to land.
//
// If the stored ID does not match what we expect, it re-saves
// the correct ID.
//
// Returns { verified: boolean, error: string | null }
// ============================================================
async function verifySubscriptionSaved(userId, expectedSubId) {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('react_subscription_id')
    // only fetch this one column -- we just need to check its value
    .eq('user_id', userId)
    .single()
    // .single() returns the row directly instead of an array

  if (error && error.code === 'PGRST116') {
    // Error code PGRST116 = no rows found = this user has no prefs row yet
    console.warn('[OneSignal] No notification_preferences row found; creating one.')

    const { error: insertError } = await supabase
      .from('notification_preferences')
      .insert({ user_id: userId, react_subscription_id: expectedSubId })
      // create a minimal row so the subscription ID has a place to live

    if (insertError) {
      return {
        verified: false,
        error: 'Could not create notification preferences row: ' + insertError.message,
      }
    }
    return { verified: true, error: null }
    // row created successfully with the correct subscription ID
  }

  if (error) {
    // Some other database error
    return { verified: false, error: 'Could not verify saved subscription: ' + error.message }
  }

  if (data?.react_subscription_id !== expectedSubId) {
    // Row exists but the stored ID does not match what we saved
    console.warn(
      '[OneSignal] Subscription ID mismatch after save.',
      'Expected:', expectedSubId,
      'Got:', data?.react_subscription_id
    )

    // Try to force-write the correct ID
    const rewrite = await _writeSubscriptionId(userId, expectedSubId)
    if (!rewrite.success) {
      return {
        verified: false,
        error: 'Subscription ID did not save correctly: ' + rewrite.error,
      }
    }
  }

  return { verified: true, error: null }
}


// ============================================================
// usePushNotifications HOOK
// ============================================================

export function usePushNotifications() {

  // ---- STATE ----

  const [isSupported, setIsSupported] = useState(false)
  // true if this browser supports push notifications at all
  // requires both serviceWorker and PushManager APIs

  const [permission, setPermission] = useState('default')
  // the browser-level notification permission:
  //   'default' = user has not been asked yet
  //   'granted' = user allowed notifications
  //   'denied'  = user blocked notifications (cannot re-prompt)

  const [isSubscribed, setIsSubscribed] = useState(false)
  // true if this device is currently opted in to OneSignal

  const [isLoading, setIsLoading] = useState(false)
  // true while subscribe or unsubscribe is running
  // used to show a spinner and disable the button

  const [error, setError] = useState(null)
  // null = no error, string = message to show the user


  // -------------------------------------------------------
  // Check current subscription status when the hook mounts.
  //
  // We read directly from the OneSignal SDK instead of Supabase
  // because the SDK always reflects the real device state.
  // A Supabase row could be stale if the user cleared browser
  // data or if the subscription expired.
  //
  // ALSO: if the device thinks it is subscribed but Supabase
  // has no react_subscription_id stored, we re-save it.
  // This repairs the common case where the DB row was wiped
  // or the initial save failed without anyone noticing.
  // -------------------------------------------------------
  useEffect(() => {
    const check = async () => {

      // Does this browser support push notifications at all?
      const supported = 'serviceWorker' in navigator && 'PushManager' in window
      // serviceWorker is required to receive push notifications in background
      // PushManager is the Web Push API that handles subscriptions
      setIsSupported(supported)

      if (!supported) return
      // no point continuing if push is not supported

      setPermission(Notification.permission)
      // Notification.permission = the current browser permission status
      // 'default' | 'granted' | 'denied'

      try {
        const OneSignal = await waitForOneSignal()

        const subscribed = OneSignal.Notifications.permission &&
                           OneSignal.User.PushSubscription.optedIn
        // .permission = has the user granted permission in the browser?
        // .optedIn    = has OneSignal marked this device as subscribed?
        // both must be true for the device to actually receive pushes
        setIsSubscribed(!!subscribed)
        // !! converts to a proper boolean (true/false not truthy/falsy)

        // If subscribed, make sure the ID is saved in Supabase
        if (subscribed) {
          const subId = OneSignal.User.PushSubscription.id
          // the unique ID OneSignal uses to identify this device

          if (subId) {
            const { data: { user } } = await supabase.auth.getUser()

            if (user) {
              let dbData = null

              try {
                const { data } = await supabase
                  .from('notification_preferences')
                  .select('react_subscription_id')
                  .eq('user_id', user.id)
                  .single()
                dbData = data
              } catch (_) {
                // row missing or RLS blocked the query
                // dbData stays null, which triggers a re-save below
              }

              if (!dbData?.react_subscription_id) {
                // The device is subscribed but the DB has no ID stored.
                // Re-save it now so notifications can actually deliver.
                console.log('[OneSignal] Device is subscribed but DB has no ID; re-saving.')
                await _writeSubscriptionId(user.id, subId)
              }
            }
          }
        }

      } catch (err) {
        console.warn('[OneSignal] Could not check subscription status:', err.message)
        setIsSubscribed(false)
        // default to false so the UI shows the "Enable" button
      }
    }

    check()
  }, [])
  // [] = run once when the component first mounts


  // -------------------------------------------------------
  // subscribe
  //
  // Asks the user for permission and opts this device in.
  // Steps:
  //   1. Ask the browser for notification permission
  //   2. Save the OneSignal subscription ID to Supabase
  //   3. Verify the ID was actually stored (safety check)
  // -------------------------------------------------------
  const subscribe = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const OneSignal = await waitForOneSignal()

      await OneSignal.Notifications.requestPermission()
      // shows the browser's native "Allow / Block" permission dialog

      const granted = OneSignal.Notifications.permission
      // read the result -- true if the user clicked Allow
      setPermission(Notification.permission)
      // also update our local state to match the browser

      if (!granted) {
        setError(
          'Notification permission was not granted. ' +
          'You can enable it in your browser or OS notification settings.'
        )
        setIsLoading(false)
        return
        // stop here if the user clicked Block
      }

      // Save the subscription ID to Supabase
      const saveResult = await saveSubscriptionId()
      if (!saveResult.success) {
        setError(saveResult.error || 'Could not save your subscription ID. Please try again.')
        setIsLoading(false)
        return
      }

      // Verify the save actually worked by reading it back
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const subId = OneSignal.User.PushSubscription.id
        if (subId) {
          const { verified, error: verifyError } = await verifySubscriptionSaved(user.id, subId)
          if (!verified) {
            setError(verifyError || 'Subscription saved by OneSignal but could not be stored in your account.')
            setIsLoading(false)
            return
          }
        }
      }

      setIsSubscribed(true)
      console.log('[OneSignal] Notifications enabled and subscription ID verified in DB.')

    } catch (err) {
      console.error('[OneSignal] Subscribe error:', err)
      setError('Could not enable notifications: ' + err.message)
    } finally {
      setIsLoading(false)
      // always turn off the loading spinner when done
    }
  }, [])
  // [] = no dependencies, this function never needs to be recreated


  // -------------------------------------------------------
  // unsubscribe
  //
  // Opts this device OUT of receiving push notifications.
  //
  // We call optOut() rather than deleting the subscription.
  // optOut() just pauses delivery without removing the device
  // record from OneSignal, so re-enabling is instant -- no
  // new permission dialog and no new subscription ID needed.
  // -------------------------------------------------------
  const unsubscribe = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const OneSignal = await waitForOneSignal()
      await OneSignal.User.PushSubscription.optOut()
      // tell OneSignal to stop sending to this device
      setIsSubscribed(false)
      console.log('[OneSignal] Notifications disabled.')

    } catch (err) {
      console.error('[OneSignal] Unsubscribe error:', err)
      setError('Could not disable notifications: ' + err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])


  // -------------------------------------------------------
  // sendTestNotification
  //
  // Sends a test push to this device through the Edge Function.
  //
  // WHY WE USE session.access_token INSTEAD OF THE ANON KEY
  //   The Edge Function's test path reads the user identity from
  //   the JWT (JSON Web Token) in the Authorization header.
  //   The anon key is a static key that does not identify a user,
  //   so the Edge Function returns 401 if it gets the anon key here.
  //   The session access token is a short-lived JWT signed by Supabase
  //   that contains the user's ID in its claims.
  //
  // PRE-FLIGHT CHECK
  //   Before calling the Edge Function, we confirm that a
  //   react_subscription_id is actually stored in Supabase.
  //   Without this check, the Edge Function just returns 400
  //   with no useful context. The check lets us show a clear
  //   "toggle off and on again" message instead.
  // -------------------------------------------------------
  const sendTestNotification = useCallback(async () => {
    setError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      // session.access_token = the JWT we need for the Edge Function auth header
      if (!session) throw new Error('Not logged in. Please refresh and try again.')

      // Check that a subscription ID is actually in the DB before calling
      // the Edge Function. Gives a much more helpful error if it is missing.
      let prefs = null
      try {
        const { data } = await supabase
          .from('notification_preferences')
          .select('react_subscription_id')
          .eq('user_id', session.user.id)
          .single()
        prefs = data
      } catch (_) {
        // row missing or query error -- prefs stays null
        // the check below will catch this
      }

      if (!prefs?.react_subscription_id) {
        setError(
          'No subscription ID found in your account. ' +
          'Toggle notifications OFF then ON again in Settings to re-register your device.'
        )
        return
        // stop here so we do not hit the Edge Function with a bad request
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-gig-reminders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          // use the user's JWT here, NOT the anon key
        },
        body: JSON.stringify({ test: true }),
        // test: true tells the Edge Function to send a test message
        // instead of processing real scheduled notifications
      })

      const data = await res.json().catch(() => ({}))
      // .catch(() => ({})) = if the response body is not valid JSON,
      // use an empty object instead of crashing

      if (!res.ok) {
        // The Edge Function returned a non-200 status
        throw new Error(data.error || `Server returned ${res.status}`)
        // data.error = the message the Edge Function sent back
      }

      console.log('[OneSignal] Test notification sent successfully.')

    } catch (err) {
      console.error('[OneSignal] Test notification error:', err)
      setError('Test failed: ' + err.message)
    }
  }, [])


  // Return everything the Settings page needs
  return {
    isSupported,          // boolean -- false if browser cannot receive push
    permission,           // 'default' | 'granted' | 'denied'
    isSubscribed,         // boolean -- true if this device is opted in
    isLoading,            // boolean -- true during subscribe/unsubscribe
    error,                // string | null -- last error message
    subscribe,            // () => Promise<void> -- enable notifications
    unsubscribe,          // () => Promise<void> -- disable notifications
    sendTestNotification, // () => Promise<void> -- send a test push
  }
}